import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Card, ChipGroup, COPY, EmptyState, PageHeader, Spinner } from "@magarac/ui";
import { calendarResultSchema } from "@magarac/shared/schemas/job";
import { dashboardSchema, type SetupStep } from "@magarac/shared/schemas/dashboard";
import { tenantStatusPanelSchema } from "@magarac/shared/schemas/tenant";
import { addDays } from "@magarac/shared/time/tz";
import { HelpIcon } from "../../components/HelpIcon";
import { LoadError } from "../../components/LoadError";
import { VisitCard } from "../../components/VisitCard";
import { api } from "../../lib/api";
import { formatLocalDate, formatMoney } from "../../lib/format";
import { useIsOffice, useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/")({
  component: HomePage,
});

function HomePage() {
  const { active, loading, loadError, retry } = useTenant();
  const office = useIsOffice();
  if (loading) return <Spinner />;
  if (loadError) return <LoadError error={loadError} onRetry={retry} />;
  if (!active) {
    return (
      <EmptyState
        title={`You are not part of a ${COPY.business} yet`}
        body="Ask the owner to invite you, then open the link in the email."
      />
    );
  }
  return office ? <Dashboard /> : <MyDay />;
}

const SETUP_LINKS: Record<SetupStep, { label: string; to: string }> = {
  business_hours: { label: "Set your business hours", to: "/settings/hours" },
  base_address: { label: "Add the address you leave from", to: "/settings/locations" },
  services: { label: "Add the services you offer", to: "/services" },
  team_members: { label: "Put people on the schedule", to: "/schedule" },
  first_customer: { label: "Add or import your customers", to: "/import" },
};

/** CRM-28 (Phase 3 part) plus the MON-9 status panel. Screen key: home */
function Dashboard() {
  const { active } = useTenant();
  const dash = useQuery({
    queryKey: ["dashboard", active?.id],
    queryFn: () => api.get("/dashboard", dashboardSchema),
    enabled: Boolean(active),
    refetchInterval: 120_000,
  });
  const status = useQuery({
    queryKey: ["tenant", active?.id, "status"],
    queryFn: () => api.get("/tenant/status", tenantStatusPanelSchema),
    enabled: Boolean(active),
  });
  const d = dash.data;
  const red = status.data?.items.filter((i) => i.state !== "green") ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={active?.name ?? "Home"}
        subtitle={d ? formatLocalDate(d.date) : undefined}
        help={<HelpIcon screen="home" />}
      />
      {dash.isLoading ? <Spinner /> : null}
      {dash.error ? <LoadError error={dash.error} onRetry={() => void dash.refetch()} /> : null}
      {d ? (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Visits today" value={String(d.todaysVisits.length)} to="/calendar" />
            <Stat label="Jobs this week" value={String(d.jobsThisWeek)} to="/jobs" />
            <Stat label="New requests" value={String(d.newRequests)} to="/requests" />
            <Stat
              label="Owed to you"
              value={formatMoney(d.outstandingBalanceCents)}
              to="/customers"
              search={{ owes: true }}
            />
          </div>
          {d.unfinishedSetup.length > 0 ? (
            <Card title="Finish setting up" className="mb-6">
              <ol className="list-decimal space-y-2 pl-5">
                {d.unfinishedSetup.map((s) => (
                  <li key={s}>
                    <Link to={SETUP_LINKS[s].to} className="text-brand-600 underline">
                      {SETUP_LINKS[s].label}
                    </Link>
                  </li>
                ))}
              </ol>
            </Card>
          ) : null}
          {red.length > 0 ? (
            <Card title="Needs attention" className="mb-6">
              <ul>
                {red.map((item) => (
                  <li key={item.key} className="flex justify-between py-1">
                    <span>{item.label}</span>
                    <span className={item.state === "yellow" ? "text-warn" : "text-danger"}>
                      {item.state === "yellow" ? "Check" : "Fix"}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          <h2 className="mb-3 text-lg font-semibold">Today</h2>
          {d.todaysVisits.length === 0 ? (
            <EmptyState
              title="No visits today"
              action={
                <Link to="/calendar" className="text-brand-600 underline">
                  Open the calendar
                </Link>
              }
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {d.todaysVisits.map((v) => (
                <VisitCard key={v.id} item={v} timezone={d.timezone} showActions={false} />
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  to,
  search,
}: {
  label: string;
  value: string;
  to: string;
  search?: Record<string, unknown>;
}) {
  return (
    <Link
      to={to}
      search={search as never}
      className="rounded-card border border-line bg-surface p-4 hover:bg-brand-50"
    >
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-sm text-muted">{label}</div>
    </Link>
  );
}

/** SCH-10: a team member's own day (or week) on the phone. Screen key: myday */
function MyDay() {
  const { active } = useTenant();
  const [range, setRange] = useState<"today" | "week">("today");
  const dash = useQuery({
    queryKey: ["dashboard", active?.id],
    queryFn: () => api.get("/dashboard", dashboardSchema),
    enabled: Boolean(active),
  });
  const today = dash.data?.date;
  const to = today ? (range === "today" ? today : addDays(today, 6)) : undefined;
  const cal = useQuery({
    queryKey: ["calendar", active?.id, today, to],
    queryFn: () => api.get("/calendar", calendarResultSchema, { query: { from: today!, to: to! } }),
    enabled: Boolean(today && to),
    refetchInterval: 60_000,
  });
  const items = cal.data?.items ?? [];
  const byDay = new Map<string, typeof items>();
  for (const item of items) {
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: cal.data!.timezone }).format(
      new Date(item.plannedStart),
    );
    (byDay.get(day) ?? byDay.set(day, []).get(day)!).push(item);
  }

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title="My day"
        subtitle={today ? formatLocalDate(today) : undefined}
        help={<HelpIcon screen="myday" />}
      />
      <div className="mb-4">
        <ChipGroup
          label="Show"
          value={range}
          onChange={setRange}
          options={[
            { value: "today", label: "Today" },
            { value: "week", label: "Next 7 days" },
          ]}
        />
      </div>
      {cal.isLoading || dash.isLoading ? <Spinner /> : null}
      {dash.error || cal.error ? (
        <LoadError
          error={dash.error ?? cal.error}
          onRetry={() => void (dash.error ? dash.refetch() : cal.refetch())}
        />
      ) : null}
      {cal.data && cal.data.lanes.length === 0 ? (
        <EmptyState
          title="You are not on the schedule yet"
          body="Ask the office to connect your login to your name on the schedule."
        />
      ) : null}
      {cal.data && cal.data.lanes.length > 0 && items.length === 0 ? (
        <EmptyState
          title={
            range === "today"
              ? "Nothing on your schedule today"
              : "Nothing on your schedule this week"
          }
        />
      ) : null}
      {[...byDay].map(([day, list]) => (
        <section key={day} className="mb-6">
          {range === "week" ? <h2 className="mb-2 font-semibold">{formatLocalDate(day)}</h2> : null}
          <div className="grid gap-3">
            {list.map((v) => (
              <VisitCard key={v.id} item={v} timezone={cal.data!.timezone} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
